import axios from 'axios';
import config from '../config/env';
import { clearTokenAndRedirectToLogin } from './roleLogin';

const API_URL = config.apiUrl;

// Helper function to get user-friendly error message
export const getErrorMessage = (error, defaultMessage = 'An error occurred') => {
  if (error.isNetworkError) {
    return 'Cannot connect to server. Please check if the backend server is running.';
  }
  if (error.isTimeoutError) {
    return 'Request timeout. Please try again.';
  }
  if (error.isNotFoundError) {
    return error.message || 'Resource not found.';
  }
  if (error.isServerError) {
    return error.message || 'Server error. Please try again later.';
  }
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  if (error.response?.data?.error) {
    return error.response.data.error;
  }
  if (error.message) {
    return error.message;
  }
  return defaultMessage;
};

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // A FormData body must not inherit this instance's default
    // 'Content-Type: application/json'. Multipart requires a boundary
    // parameter that only the browser can generate, and it only does so when
    // no Content-Type is set — otherwise the request goes out labelled JSON,
    // multer parses no file, and the upload fails with "No file uploaded".
    // Deleting the header here fixes every FormData caller at once, instead of
    // each call site having to remember to override it.
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      if (config.headers && typeof config.headers.delete === 'function') {
        config.headers.delete('Content-Type'); // axios v1 AxiosHeaders
      } else if (config.headers) {
        delete config.headers['Content-Type'];
        delete config.headers['content-type'];
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle response errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Handle network errors (no response from server)
    if (!error.response) {
      console.error('Network Error:', error.message);

      // CORS errors appear as network errors
      if (error.message === 'Network Error' || error.code === 'ERR_NETWORK' || error.message === 'Failed to fetch') {
        // Anchored: a bare .replace('/api', '') matches the "//api" inside
        // https://api.testgate.io and renders the origin as "https:/.testgate.io",
        // which reads like a broken config and sends people chasing the wrong bug.
        const serverOrigin = API_URL.replace(/\/api\/?$/, '');
        const networkError = new Error(`Failed to connect to server. Please ensure the backend server is running on ${serverOrigin}`);
        networkError.isNetworkError = true;
        networkError.originalError = error;
        console.error('Failed to connect to API server. This might be a CORS issue or the server is not running on', API_URL);
        console.warn('Token preserved - this appears to be a network/CORS error, not an authentication failure');
        console.warn('Please ensure the backend server is running on port 5001');
        console.warn('Start the server with: cd server && npm run dev');
        return Promise.reject(networkError);
      }

      // Handle timeout errors
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        const timeoutError = new Error('Request timeout. The server took too long to respond.');
        timeoutError.isTimeoutError = true;
        timeoutError.originalError = error;
        return Promise.reject(timeoutError);
      }

      // DO NOT clear token on network errors - it might just be CORS or temporary network issue
      return Promise.reject(error);
    }

    // Handle 401 Unauthorized errors
    if (error.response?.status === 401) {
      // Only clear token if we got a proper 401 response from the server
      // This means the server processed the request and rejected the token
      const errorMessage = error.response?.data?.message || '';

      // Check if this is an actual authentication error vs CORS
      if (errorMessage.toLowerCase().includes('token') ||
        errorMessage.toLowerCase().includes('unauthorized') ||
        errorMessage.toLowerCase().includes('authorization denied') ||
        errorMessage.toLowerCase().includes('invalid token')) {
        // This is a genuine authentication failure
        if (process.env.NODE_ENV === 'development') {
          console.warn('Authentication failed - token cleared:', errorMessage);
        }
        clearTokenAndRedirectToLogin();
      } else {
        // Might be CORS or other issue, preserve token
        console.warn('Received 401 but preserving token. Error:', errorMessage);
      }
    }

    // Handle 404 Not Found errors
    if (error.response?.status === 404) {
      const notFoundError = new Error(error.response?.data?.message || 'Resource not found');
      notFoundError.isNotFoundError = true;
      notFoundError.status = 404;
      return Promise.reject(notFoundError);
    }

    // Handle 429 Too Many Requests
    if (error.response?.status === 429) {
      const rateLimitError = new Error(error.response?.data?.message || 'Too many requests. Please wait a moment and try again.');
      rateLimitError.isRateLimitError = true;
      rateLimitError.status = 429;
      rateLimitError.retryAfter = error.response?.headers?.['retry-after'] || 5;
      console.warn('Rate limited:', error.response?.data?.message || 'Too many requests');
      return Promise.reject(rateLimitError);
    }

    // Handle 500 Server errors
    if (error.response?.status >= 500) {
      let serverMessage;
      const data = error.response?.data;
      if (data && typeof Blob !== 'undefined' && data instanceof Blob) {
        try {
          const text = await data.text();
          const parsed = JSON.parse(text);
          serverMessage = parsed.message || parsed.error;
        } catch {
          /* blob was not JSON */
        }
      } else {
        serverMessage = data?.message || data?.error;
      }
      const serverError = new Error(serverMessage || 'Server error. Please try again later.');
      serverError.isServerError = true;
      serverError.status = error.response?.status;
      serverError.response = error.response;
      return Promise.reject(serverError);
    }

    return Promise.reject(error);
  }
);

export default api;

